import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramTotalPressure } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.totalPressure.v1';

type Fields = { staticPressure: string; velocityPressure: string; totalPressure: string };
const EMPTY: Fields = { staticPressure: '', velocityPressure: '', totalPressure: '' };

/**
 * Total Pressure — Pt = Ps + Pv
 */
const AirflowTotalPressureScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill any 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const ps = toImp(fields.staticPressure, 'pressure');
    const pv = toImp(fields.velocityPressure, 'pressure');
    const pt = toImp(fields.totalPressure, 'pressure');

    if (fields.totalPressure === '') {
      setSolvedFor('totalPressure');
      set({ totalPressure: fromImp(ps + pv, 'pressure', 2) });
    } else if (fields.staticPressure === '') {
      setSolvedFor('staticPressure');
      set({ staticPressure: fromImp(pt - pv, 'pressure', 2) });
    } else if (fields.velocityPressure === '') {
      setSolvedFor('velocityPressure');
      set({ velocityPressure: fromImp(pt - ps, 'pressure', 2) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.staticPressure && { label: 'Static Pressure', value: fields.staticPressure, unit: unit('pressure'), emphasis: solvedFor === 'staticPressure' },
    fields.velocityPressure && { label: 'Velocity Pressure', value: fields.velocityPressure, unit: unit('pressure'), emphasis: solvedFor === 'velocityPressure' },
    fields.totalPressure && { label: 'Total Pressure', value: fields.totalPressure, unit: unit('pressure'), emphasis: solvedFor === 'totalPressure' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Total Pressure"
      subtitle="Calculate total pressure, static pressure, or velocity pressure relationships"
      formula={
        <>
          <FormulaLine>Pt = Ps + Pv</FormulaLine>
          <FormulaNote>
            Pt = Total Pressure, Ps = Static Pressure, Pv = Velocity Pressure (all in {unit('pressure')})
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: Pt = Ps + Pv"
      diagram={<DiagramTotalPressure />}
      outputSlots={{
        static_pressure_iwg: fields.staticPressure ? toImp(fields.staticPressure, 'pressure') : undefined,
        velocity_pressure_iwg: fields.velocityPressure ? toImp(fields.velocityPressure, 'pressure') : undefined,
        total_pressure_iwg: fields.totalPressure ? toImp(fields.totalPressure, 'pressure') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.static_pressure_iwg != null && !fields.staticPressure) {
          patch.staticPressure = fromImp(values.static_pressure_iwg, 'pressure', 4);
        }
        if (values.velocity_pressure_iwg != null && !fields.velocityPressure) {
          patch.velocityPressure = fromImp(values.velocity_pressure_iwg, 'pressure', 4);
        }
        if (values.total_pressure_iwg != null && !fields.totalPressure) {
          patch.totalPressure = fromImp(values.total_pressure_iwg, 'pressure', 4);
        }
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Static Pressure (${unit('pressure')})`}
          value={fields.staticPressure}
          onChangeText={(v) => set({ staticPressure: v })}
          rightText={unit('pressure')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Velocity Pressure (${unit('pressure')})`}
          value={fields.velocityPressure}
          onChangeText={(v) => set({ velocityPressure: v })}
          rightText={unit('pressure')}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Total Pressure (${unit('pressure')})`}
          value={fields.totalPressure}
          onChangeText={(v) => set({ totalPressure: v })}
          rightText={unit('pressure')}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default AirflowTotalPressureScreen;
